import React, { memo, useCallback, useEffect, useState } from "react"
import { $isCodeNode, CODE_LANGUAGE_FRIENDLY_NAME_MAP } from "@lexical/code"
import {
  $isListNode,
  ListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
} from "@lexical/list"

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $isHeadingNode } from "@lexical/rich-text"
import { $getSelectionStyleValueForProperty, $patchStyleText } from "@lexical/selection"
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from "@lexical/utils"
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
} from "lexical"
import {
  BulletList,
  NumberedList,
  CheckList,
  ColorText,
  BaseTextIcon,
  Heading1Icon,
  Heading3Icon,
  Heading2Icon,
  QuoteIcon,
  CodeIcon,
  TextIcon,
  BackgroundText,
  CommentTextIcon,
} from "../theme/editorIcons"

import styles from "./toolbarPlugin.module.css"
import { ActionsPopover, Ellipsis, Tooltip, useAppContext, Button } from "@dit/core-frontend"
import { Box, Text } from "@mos-cat/ds"
import { ArrowDown } from "@mos-cat/ds-icons"
import {
  ChangeColorDropDown,
  FontSizeDropDown,
  OtherActionsDropDown,
  BlockFormatDropDown,
  TextAlignDropDown,
  TextStyleDropDown,
  UndoRedoButtons,
  TableOfContentButton,
  InsertLinkButton,
  TextStyleButtons,
  buttonProps,
  SearchButton,
} from "@editor/components/ToolbarDropDowns"
import { usePageFormContext } from "@src/components/contexts/PageFormContext"
import { useZoneContext } from "@components/contexts/ZoneContext"
import { useParams } from "react-router-dom"

const blockTypeToBlockName = {
  paragraph: {
    value: "Обычный",
    icon: <BaseTextIcon />,
  },
  h1: {
    value: "Заголовок 1",
    icon: <Heading1Icon />,
  },
  h2: {
    value: "Заголовок 2",
    icon: <Heading2Icon />,
  },
  h3: {
    value: "Заголовок 3",
    icon: <Heading3Icon />,
  },
  quote: {
    value: "Цитата",
    icon: <QuoteIcon />,
  },
  code: {
    value: "Блок кода",
    icon: <CodeIcon />,
  },
}
//Подготовка для добавления шрифтов
const FONT_FAMILY_OPTIONS = [
  ["Arial", "Arial"],
  ["Courier New", "Courier New"],
  ["Georgia", "Georgia"],
  ["Times New Roman", "Times New Roman"],
  ["Trebuchet MS", "Trebuchet MS"],
  ["Verdana", "Verdana"],
]

function getCodeLanguageOptions() {
  const options = []

  for (const [lang, friendlyName] of Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP)) {
    options.push([lang, friendlyName])
  }

  return options
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions()

const FontFamilyDropDown = memo(({ editor, value, style, disabled, isMobileView }) => {
  return (
    <ActionsPopover
      disabled={disabled}
      portalContainer={document.body}
      content={
        <Tooltip content="Тип шрифта">
          <Button
            {...buttonProps}
            kind="borderless"
            vertSize="30"
            width={["unset", "100px"]}
            display="flex"
            alignItems="center"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gridGap="2px"
              width="100%"
            >
              {isMobileView && (
                <Text>
                  <TextIcon />
                </Text>
              )}

              {!isMobileView && (
                <Text as={Box} maxWidth="75px" display="flex" fontSize="fs-14">
                  <Ellipsis
                    additionalStyles={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 1,
                      display: "inline-block",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {value || FONT_FAMILY_OPTIONS[0][0]}
                  </Ellipsis>
                </Text>
              )}

              <Box justifySelf="flex-end" fontSize="fs-14">
                <Text fontSize="fs-14">
                  <ArrowDown />
                </Text>
              </Box>
            </Box>
          </Button>
        </Tooltip>
      }
    >
      {(buttonProps, hide) => (
        <>
          {FONT_FAMILY_OPTIONS.map(([option, text]) => (
            <Button
              {...buttonProps}
              key={text}
              {...buttonProps}
              onClick={() => {
                editor.update(() => {
                  const selection = $getSelection()
                  if (selection !== null) {
                    $patchStyleText(selection, {
                      [style]: option,
                    })
                    const nodes = selection.getNodes()
                    if (nodes?.length) {
                      nodes[nodes.length - 1].selectEnd()
                    }
                    hide()
                  }
                })
              }}
            >
              {text}
            </Button>
          ))}
        </>
      )}
    </ActionsPopover>
  )
})
FontFamilyDropDown.displayName = "FontFamilyDropDown"
const FormatedList = memo(({ editor, blockType, disabled }) => {
  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatCheckList = () => {
    if (blockType !== "check") {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  return (
    <Box display="flex" alignItems="center" gridGap="10px">
      <Tooltip content="Маркировочный список">
        <Button
          {...buttonProps}
          disabled={disabled}
          width="16px"
          kind="borderless"
          onClick={formatBulletList}
        >
          <BulletList />
        </Button>
      </Tooltip>
      <Tooltip content="Нумерованный список">
        <Button {...buttonProps} disabled={disabled} kind="borderless" onClick={formatNumberedList}>
          <NumberedList />
        </Button>
      </Tooltip>
      <Tooltip content="Чеклист">
        <Button {...buttonProps} disabled={disabled} kind="borderless" onClick={formatCheckList}>
          <CheckList />
        </Button>
      </Tooltip>
    </Box>
  )
})
FormatedList.displayName = "FormatedList"

export const ToolbarPlugin = memo(({ setIsLinkEditMode, onFileUploadStart }) => {
  const [editor] = useLexicalComposerContext()
  const [activeEditor, setActiveEditor] = useState(
    editor?._parentEditor ? editor?._parentEditor : editor,
  )
  const [fontSize, setFontSize] = useState("12pt")
  const [blockType, setBlockType] = useState("paragraph")
  const [selectedElementKey, setSelectedElementKey] = useState(null)
  const [fontColor, setFontColor] = useState("#000")
  const [bgColor, setBgColor] = useState("#fff")
  const [fontFamily, setFontFamily] = useState("Arial")
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isLink, setIsLink] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [isEditable, setIsEditable] = useState(() => editor.isEditable())
  const { togleTextCommentsShow } = useZoneContext()
  const { isMobileView } = useAppContext()
  const { isAutoSaveEnabled } = usePageFormContext()
  const params = useParams()

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent()
              return parent !== null && $isRootOrShadowRoot(parent)
            })

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow()
      }

      const elementKey = element.getKey()
      const elementDOM = activeEditor.getElementByKey(elementKey)

      setIsBold(selection.hasFormat("bold"))
      setIsItalic(selection.hasFormat("italic"))
      setIsUnderline(selection.hasFormat("underline"))
      setIsStrikethrough(selection.hasFormat("strikethrough"))

      // const node = getSelectedNode(selection);
      // const parent = node.getParent();
      // if ($isLinkNode(parent) || $isLinkNode(node)) {
      //   setIsLink(true);
      // } else {
      //   setIsLink(false);
      // }

      // const tableNode = $findMatchingParent(node, $isTableNode);
      // if ($isTableNode(tableNode)) {
      //   setRootType('table');
      // } else {
      //   setRootType('root');
      // }

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey)
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode)
          const type = parentList ? parentList.getListType() : element.getListType()
          setBlockType(type)
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType()
          if (type in blockTypeToBlockName) {
            setBlockType(type)
          }
        }
      }

      setFontSize($getSelectionStyleValueForProperty(selection, "font-size", "12pt"))
      setFontFamily($getSelectionStyleValueForProperty(selection, "font-family", "Arial"))
      setFontColor($getSelectionStyleValueForProperty(selection, "color", "#000"))
      setBgColor($getSelectionStyleValueForProperty(selection, "background-color", "#fff"))
    }
  }, [activeEditor])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        updateToolbar()
        setActiveEditor(newEditor?._parentEditor ? newEditor._parentEditor : newEditor)
        return false
      },
      COMMAND_PRIORITY_CRITICAL,
    )
  }, [editor, updateToolbar])

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable)
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      activeEditor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload)
          return false
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      activeEditor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload)
          return false
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    )
  }, [activeEditor, editor, updateToolbar])

  //цвет текста

  const applyStyleText = useCallback(
    (styles, skipHistoryStack) => {
      activeEditor.update(
        () => {
          const selection = $getSelection()
          if (selection !== null) {
            $patchStyleText(selection, styles)
          }
        },
        skipHistoryStack ? { tag: "historic" } : {},
      )
    },
    [activeEditor],
  )
  const onFontColorSelect = useCallback(
    (value, skipHistoryStack) => {
      applyStyleText({ color: value }, skipHistoryStack)
    },
    [applyStyleText],
  )

  const onBgColorSelect = useCallback(
    (value, skipHistoryStack) => {
      applyStyleText({ "background-color": value }, skipHistoryStack)
    },
    [applyStyleText],
  )

  const onCodeLanguageSelect = useCallback(
    (value) => {
      activeEditor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey)
          if ($isCodeNode(node)) {
            node.setLanguage(value)
          }
        }
      })
    },
    [activeEditor, selectedElementKey],
  )

  const isDisabledToolbar = !params?.templateId && (!isEditable || !isAutoSaveEnabled)

  return (
    <div className={styles.toolbarWrapper}>
      <div className={styles.toolbarItems}>
        <UndoRedoButtons
          activeEditor={activeEditor}
          isEditable={isEditable}
          canRedo={canRedo}
          canUndo={canUndo}
        />

        {blockType !== "code" && (
          <>
            <div className={styles.separator}></div>
            <TableOfContentButton activeEditor={activeEditor} isEditable={isEditable} />
            <div className={styles.separator}></div>
          </>
        )}

        <BlockFormatDropDown
          disabled={isDisabledToolbar}
          blockType={blockType}
          editor={editor}
          value={blockType}
          label={blockType}
          isMobileView={isMobileView}
        />
        <div className={styles.separator}></div>
        {blockType === "code" ? (
          <>
            <ActionsPopover
              disabled={isDisabledToolbar}
              portalContainer={document.body}
              content={
                <Button
                  {...buttonProps}
                  kind="borderless"
                  vertSize="30"
                  width="70px"
                  display="flex"
                  justifyContent="start"
                  alignItems="center"
                >
                  <Box display="flex">
                    <Text fontSize="fs-14">язык</Text>
                    <Text fontSize="fs-14">
                      <ArrowDown />
                    </Text>
                  </Box>
                </Button>
              }
            >
              {(buttonProps) => (
                <>
                  {CODE_LANGUAGE_OPTIONS.map(([value, name]) => (
                    <Button
                      {...buttonProps}
                      {...buttonProps}
                      key={value}
                      onClick={() => onCodeLanguageSelect(value)}
                    >
                      {name}
                    </Button>
                  ))}
                </>
              )}
            </ActionsPopover>
          </>
        ) : (
          <>
            <div className={styles.separator}></div>
            {/* <FontDropDown
            disabled={!isEditable}
            style={"font-size"}
            value={fontSize}
            editor={editor}
            isMobileView={isMobileView}
          /> */}
            <FontSizeDropDown
              disabled={isDisabledToolbar}
              style={"font-size"}
              value={fontSize}
              editor={editor}
              isMobileView={isMobileView}
            />
            <div className={styles.separator}></div>
            <FontFamilyDropDown
              disabled={isDisabledToolbar}
              style={"font-family"}
              value={fontFamily}
              editor={editor}
              isMobileView={isMobileView}
            />
            <div className={styles.separator}></div>
            <TextStyleButtons
              activeEditor={activeEditor}
              isEditable={isEditable}
              isBold={isBold}
              isItalic={isItalic}
              isUnderline={isUnderline}
              styles={styles}
              blockType={blockType}
            />

            <div className={styles.separator}></div>
            <Box paddingRight="10px" display="flex" alignItems="center">
              <InsertLinkButton
                activeEditor={activeEditor}
                isEditable={isEditable}
                setIsLinkEditMode={setIsLinkEditMode}
                isLink={isLink}
              />
            </Box>
            <FormatedList disabled={isDisabledToolbar} blockType={blockType} editor={editor} />
            <div className={styles.separator}></div>
            <ChangeColorDropDown
              disabled={isDisabledToolbar}
              dropDownIcon={ColorText}
              color={fontColor}
              onChange={onFontColorSelect}
            />
            <div className={styles.separator}></div>
            <ChangeColorDropDown
              disabled={isDisabledToolbar}
              dropDownIcon={BackgroundText}
              color={bgColor}
              onChange={onBgColorSelect}
              tooltipText="Изменение фона текста"
            />
            <div className={styles.separator}></div>
            <TextStyleDropDown disabled={isDisabledToolbar} activeEditor={activeEditor} />
            <div className={styles.separator}></div>
            <TextAlignDropDown disabled={isDisabledToolbar} activeEditor={activeEditor} />
            <div className={styles.separator}></div>
            <OtherActionsDropDown
              disabled={isDisabledToolbar}
              activeEditor={activeEditor}
              onFileUploadStart={onFileUploadStart}
            />
            <div className={styles.separator}></div>
            <SearchButton isEditable={isEditable} activeEditor={activeEditor} />
          </>
        )}
      </div>
      <Box padding="0px 10px" minHeight="35px" display="flex" alignItems="center">
        <Tooltip content="Панель комментариев">
          <Button
            onClick={togleTextCommentsShow}
            {...buttonProps}
            justifySelf="end"
            kind="borderless"
            // vertSize="30px"
            // width="30px"
            // height="30px"
            styles={{ fontSize: "14px" }}
          >
            {<CommentTextIcon size="16" />}
          </Button>
        </Tooltip>
      </Box>
    </div>
  )
})

ToolbarPlugin.displayName = "ToolbarPlugin"
